import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { logApiError, logUnexpectedError } from "../Utils/apiError";

// Normalize API base so we never hit /api/api
const RAW = process.env.REACT_APP_API_BASE || "";
const API_BASE = RAW.replace(/\/+$/, "");
const APIv1 = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;

export default function DealerResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const strongEnough = useMemo(() => pw.length >= 8, [pw]);
  const match = pw && confirm && pw === confirm;

  useEffect(() => {
    if (!token) {
      setErr("Invalid or missing reset token.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token || !match || !strongEnough) return;
    setSubmitting(true);
    setErr("");
    setMsg("");
    try {
      const res = await fetch(`${APIv1}/dealer/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: pw }),
      });
      if (!res.ok) {
        await logApiError(res, "Dealer password reset failed");
        throw new Error(`HTTP ${res.status}`);
      }
      setMsg("Password has been reset. You can sign in now.");
    } catch (e) {
      logUnexpectedError(e, "Dealer password reset request failed");
      setErr("Reset link is invalid or expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white border rounded-xl p-6">
        <h1 className="text-2xl font-semibold">Set a new password</h1>
        <p className="text-sm text-gray-600 mt-2">
          Choose a strong password you haven’t used before.
        </p>

        <label className="block text-sm text-gray-700 mt-4 mb-1">New password</label>
        <input
          type="password"
          className={`w-full border rounded-lg px-3 py-2 ${strongEnough ? "border-gray-300" : "border-red-300"}`}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          required
        />

        <label className="block text-sm text-gray-700 mt-3 mb-1">Confirm new password</label>
        <input
          type="password"
          className={`w-full border rounded-lg px-3 py-2 ${match || !confirm ? "border-gray-300" : "border-red-300"}`}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter password"
          autoComplete="new-password"
          required
        />
        {!match && confirm && (
          <div className="text-sm text-red-600 mt-1">Passwords do not match.</div>
        )}

        {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
        {msg && <div className="text-green-700 text-sm mt-2">{msg}</div>}

        <button
          type="submit"
          disabled={!token || !match || !strongEnough || submitting}
          className="mt-4 w-full bg-blue-600 text-white rounded-lg px-4 py-2 disabled:opacity-50 hover:bg-blue-700"
        >
          {submitting ? "Updating…" : "Reset password"}
        </button>

        <div className="text-sm text-gray-600 mt-4 text-center">
          <Link className="text-blue-600 underline" to="/dealer/login">
            Back to login
          </Link>
        </div>
      </form>
    </div>
  );
}
